/**
 * Overwrite Confirmation Dialog Component
 *
 * Displays a confirmation dialog for high-risk operations like key replacement
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  makeStyles
} from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import WarningIcon from '@material-ui/icons/Warning';

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: 'flex',
    alignItems: 'center'
  },
  warningIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.warning.main
  },
  errorAlert: {
    marginBottom: theme.spacing(2)
  },
  infoList: {
    paddingLeft: theme.spacing(2)
  },
  checkboxLabel: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  confirmButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.error.dark
    }
  }
}));

/**
 * Overwrite Confirmation Dialog for PIV slots
 */
function OverwriteConfirmationDialog({ open, onClose, onConfirm, slotInfo, type = 'piv' }) {
  const classes = useStyles();
  const [confirmText, setConfirmText] = useState('');
  const [understood, setUnderstood] = useState(false);

  const isValid = confirmText === 'CONFIRM' && understood;

  const handleClose = () => {
    setConfirmText('');
    setUnderstood(false);
    onClose();
  };

  const handleConfirm = () => {
    if (isValid) {
      setConfirmText('');
      setUnderstood(false);
      onConfirm();
    }
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Unknown';
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box className={classes.dialogTitle}>
          <WarningIcon className={classes.warningIcon} />
          Confirm Key Replacement
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="error" className={classes.errorAlert}>
          <AlertTitle>Irreversible Action</AlertTitle>
          You are about to replace the existing key{type === 'piv' && ` in slot ${slotInfo.name}`}.
          This action cannot be undone and may result in permanent data loss.
        </Alert>

        {/* PIV Slot Information */}
        {type === 'piv' && slotInfo.certInfo && (
          <>
            <Typography variant="body1" gutterBottom>
              Current certificate information:
            </Typography>
            <Box component="ul" className={classes.infoList}>
              <li>
                <strong>Subject:</strong> {slotInfo.certInfo.subject || 'Unknown'}
              </li>
              <li>
                <strong>Issuer:</strong> {slotInfo.certInfo.issuer || 'Unknown'}
              </li>
              <li>
                <strong>Algorithm:</strong> {slotInfo.certInfo.algorithm || 'Unknown'}{' '}
                ({slotInfo.certInfo.keySize || 'Unknown'} bits)
              </li>
              <li>
                <strong>Valid until:</strong>{' '}
                {slotInfo.certInfo.validTo ? formatDate(slotInfo.certInfo.validTo) : 'Unknown'}
              </li>
              <li>
                <strong>Fingerprint:</strong>{' '}
                {slotInfo.certInfo.fingerprint
                  ? slotInfo.certInfo.fingerprint.substring(0, 16) + '...'
                  : 'N/A'}
              </li>
            </Box>
          </>
        )}

        {/* OpenPGP Key Information */}
        {type === 'openpgp' && slotInfo.keyInfo && (
          <>
            <Typography variant="body1" gutterBottom>
              Current key information:
            </Typography>
            <Box component="ul" className={classes.infoList}>
              <li>
                <strong>Key Type:</strong> {slotInfo.name}
              </li>
              <li>
                <strong>Algorithm:</strong> {slotInfo.keyInfo.algorithm || 'Unknown'}
              </li>
              <li>
                <strong>Key Size:</strong> {slotInfo.keyInfo.keySize || 'Unknown'}
              </li>
              <li>
                <strong>Fingerprint:</strong> {slotInfo.keyInfo.fingerprint || 'N/A'}
              </li>
            </Box>
          </>
        )}

        {/* Consequences Warning */}
        <Alert severity="warning" style={{ marginTop: 16, marginBottom: 16 }}>
          <AlertTitle>Consequences</AlertTitle>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li>The existing private key will be permanently deleted</li>
            {type === 'piv' && <li>The existing certificate will be permanently deleted</li>}
            {type === 'openpgp' && (
              <li>Any data encrypted with this key will become inaccessible</li>
            )}
            <li>This action cannot be reversed</li>
            <li>You must have a backup if you need to preserve access</li>
          </ul>
        </Alert>

        {/* Understanding Checkbox */}
        <FormControlLabel
          control={
            <Checkbox checked={understood} onChange={(e) => setUnderstood(e.target.checked)} />
          }
          label={`I understand that this action will permanently delete the existing key${
            type === 'piv' ? ' and any associated certificates' : ''
          }`}
          className={classes.checkboxLabel}
        />

        {/* Confirmation Text Input */}
        <TextField
          fullWidth
          label="Type 'CONFIRM' to proceed"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
          error={confirmText !== '' && confirmText !== 'CONFIRM'}
          helperText={
            confirmText !== '' && confirmText !== 'CONFIRM'
              ? "Please type exactly: CONFIRM"
              : "This is a safety measure to prevent accidental deletions"
          }
          variant="outlined"
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="default">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          className={classes.confirmButton}
          variant="contained"
          disabled={!isValid}
        >
          Replace Key
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default OverwriteConfirmationDialog;
